import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, asset, formatUSD } from "../lib/api";
import { TimelapseCard } from "../components/TimelapseCard";
import { WeddingMosaic } from "../components/WeddingMosaic";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [all, setAll] = useState([]);
  const [weddingImgs, setWeddingImgs] = useState([]);

  useEffect(() => {
    document.title = "Scott Burton Photography — Weddings, Sports & Fine-Art Prints";
    api.get("/timelapses").then(({ data }) => {
      setAll(data);
      setFeatured(data.filter((d) => d.featured));
    });
    api.get("/photos", { params: { category: "Wedding", portfolio: true } })
      .then(({ data }) => setWeddingImgs([...data].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).map((p) => p.image_url).slice(0, 64)));
  }, []);

  return (
    <div>
      {/* HERO — wedding collage behind the name */}
      <section className="relative h-screen w-full overflow-hidden">
        <WeddingMosaic images={weddingImgs.length ? weddingImgs : (all.map((t) => t.thumbnail_url))} tiles={64} overlay={0.68}>
          <div className="text-center max-w-3xl animate-fade-up">
            <div className="overline-label mb-5">Oxford, Mississippi · Weddings · Sport · Fine-Art Prints</div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.9]">
              Scott Burton<br />Photography
            </h1>
            <p className="mt-6 text-neutral-300 max-w-xl mx-auto text-base">
              Twenty years behind the lens — from Sports Illustrated and ESPN to over a thousand weddings worldwide.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Link to="/weddings" data-testid="hero-weddings-cta" className="btn-amber rounded-full px-6 py-3 flex items-center gap-2">
                See the weddings <ArrowRight size={16} />
              </Link>
              <Link to="/portfolio" data-testid="hero-portfolio-cta" className="border border-white/25 rounded-full px-6 py-3 flex items-center gap-2 hover:border-amber-glow/60 hover:text-amber-glow transition-colors">
                Full portfolio
              </Link>
            </div>
          </div>
        </WeddingMosaic>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline-label mb-2">Featured sequences</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light">Recent captures</h2>
          </div>
          <Link to="/gallery" className="hidden sm:flex items-center gap-2 text-sm text-neutral-300 hover:text-amber-glow link-underline">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {(featured.length ? featured : all).slice(0, 6).map((tl, i) => (
            <TimelapseCard key={tl.id} tl={tl} index={i} />
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="overline-label mb-6">Explore by mood</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["Sunrise", "Sunset", "Night Sky", "Weather"].map((c) => (
            <Link key={c} to={`/gallery?category=${c}`} data-testid={`home-cat-${c}`}
              className="group relative h-40 rounded-lg border border-white/10 overflow-hidden flex items-end p-5 bg-obsidian-800 hover:border-amber-glow/50 transition-colors">
              <span className="font-serif text-2xl font-light">{c}</span>
              <ArrowRight className="absolute top-4 right-4 text-amber-glow opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
            </Link>
          ))}
        </div>
      </section>

      {/* PRINTS TEASER */}
      <section className="border-y border-white/8 bg-obsidian-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline-label mb-3">Fulfilled by Miller's Professional Imaging</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light leading-tight">
              Any frame. Any size.<br />Printed to last a century.
            </h2>
            <p className="mt-5 text-neutral-400 max-w-md">
              Fine art paper, gallery canvas wraps, and HD metal prints — produced by one of the most
              respected professional labs in the country and shipped to your door.
            </p>
            <Link to="/shop" className="inline-flex mt-8 btn-amber rounded-full px-6 py-3 items-center gap-2">
              Shop prints <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {all.slice(0, 4).map((tl) => (
              tl.thumbnail_url && <img key={tl.id} src={asset(tl.thumbnail_url)} alt="" className="rounded-lg h-48 w-full object-cover border border-white/10" />
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-28 text-center">
        <div className="overline-label mb-4">The photographer</div>
        <p className="font-serif text-3xl lg:text-4xl font-light leading-snug">
          “From the sidelines of a championship to the quiet second before a first kiss — I use long
          lenses to seize once-in-a-lifetime moments with honesty and grace.”
        </p>
        <Link to="/about" className="inline-block mt-8 text-sm text-amber-glow link-underline">Read Scott's story</Link>
      </section>
    </div>
  );
}
